import { ReactElement } from 'react';
import { ContainerCard } from '../ContainerCard/ContainerCard';
import { Paragraph } from '../Paragraph/Paragraph';
import { WalletIcon } from '@navikt/aksel-icons';
import type { LeadQuality } from '../../api/queries';
import { formatNumber } from '../../utils/numberFormatters';
import classes from './QualityPipelineCard.module.css';

interface QualityData {
  quality: LeadQuality | 'UNSET';
  count: number;
  estimatedMonthlyValueNOK: number;
  estimatedYearlyValueNOK: number;
}

interface QualityPipelineCardProps {
  byQuality: QualityData[];
}

const QUALITY_CONFIG: Record<LeadQuality | 'UNSET', { label: string; color: string }> = {
  HOT: { label: 'Varme leads', color: 'var(--a-surface-success)' },
  WARM: { label: 'Lunkne leads', color: 'var(--a-surface-warning)' },
  COLD: { label: 'Kalde leads', color: 'var(--a-surface-danger)' },
  UNSET: { label: 'Ikke satt', color: 'var(--a-surface-neutral)' },
};

const QualityRow = ({ data }: { data: QualityData }): ReactElement => {
  const config = QUALITY_CONFIG[data.quality];

  return (
    <div className={classes.qualityRow} data-quality={data.quality}>
      <div className={classes.qualityContent}>
        <div className={classes.qualityHeader}>
          <div className={classes.qualityIndicator} style={{ backgroundColor: config.color }} />
          <div className={classes.qualityLabelWrapper}>
            <div className={classes.qualityLabel}>{config.label}</div>
            <div className={classes.qualityCount}>{data.count} {data.count === 1 ? 'lead' : 'leads'}</div>
          </div>
        </div>
        <div className={classes.qualityValues}>
          <div className={classes.valueItem}>
            <div className={classes.valueLabel}>Månedlig</div>
            <div className={classes.valueAmount}>{formatNumber(data.estimatedMonthlyValueNOK)} kr</div>
          </div>
          <div className={classes.valueDivider} />
          <div className={classes.valueItem}>
            <div className={classes.valueLabel}>Årlig</div>
            <div className={classes.valueAmount}>{formatNumber(data.estimatedYearlyValueNOK)} kr</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const QualityPipelineCard = ({ byQuality }: QualityPipelineCardProps): ReactElement => {
  const order: Record<LeadQuality | 'UNSET', number> = { HOT: 0, WARM: 1, COLD: 2, UNSET: 3 };

  const sortedQuality = [...byQuality]
    .filter((data) => data.count > 0)
    .sort((a, b) => order[a.quality] - order[b.quality]);

  if (sortedQuality.length === 0) {
    return (
      <ContainerCard>
        <ContainerCard.Title icon={<WalletIcon />}>Pipeline etter lead-kvalitet</ContainerCard.Title>
        <ContainerCard.Body>
          <Paragraph size="sm" className={classes.emptyState}>
            Ingen leads med satt kvalitet ennå. Sett kvalitet på leads for å se fordeling.
          </Paragraph>
        </ContainerCard.Body>
      </ContainerCard>
    );
  }

  return (
    <ContainerCard>
      <ContainerCard.Title icon={<WalletIcon />}>Pipeline etter lead-kvalitet</ContainerCard.Title>
      <ContainerCard.Body>
        <div className={classes.qualityList}>
          {sortedQuality.map((data) => (
            <QualityRow key={data.quality} data={data} />
          ))}
        </div>
      </ContainerCard.Body>
    </ContainerCard>
  );
};
